import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-ereace-dark">
      {/* Simple Header */}
      <nav className="fixed top-0 w-full bg-ereace-dark/90 backdrop-blur-md border-b border-ereace-light-gray z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex-shrink-0">
              <span className="text-2xl font-bold text-white">Ereace</span>
            </Link>
            <Link 
              to="/" 
              className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-ereace-gray rounded-xl p-8 md:p-12 border border-ereace-light-gray">
            <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-gray-400 mb-8">Effective date: October 11, 2025</p>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
              <p className="text-gray-300 leading-relaxed">
                Ereace ("we", "us", "our") values your privacy. This Privacy Policy describes how we collect, use, store, delete, and protect personal data that you voluntarily provide when contacting us through our form or email. We aim to minimize data retention and only keep data for a limited time.
              </p>
            </section>

            {/* Data Controller */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. Data Controller</h2>
              <p className="text-gray-300 leading-relaxed mb-3">
                The data controller for your personal data is:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Name / Company: Ereace</li>
                <li>Contact email: info@ereace.es</li>
                <li>Website: https://ereace.es</li>
              </ul>
            </section>

            {/* What Personal Data We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. What Personal Data We Collect</h2>
              <p className="text-gray-300 leading-relaxed mb-3">
                We only collect the minimal personal data necessary for contact:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Your email address</li>
                <li>Message content</li>
                <li>Timestamp (date/time of submission)</li>
              </ul>
            </section>

            {/* Purpose and Legal Basis */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Purpose and Legal Basis</h2>
              <p className="text-gray-300 leading-relaxed mb-3">
                We process this data solely for the purpose of communicating with you (responding to your message, clarifying, providing information). The legal basis is consent, which you give by submitting the form/email.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We do not use the data for marketing, profiling, or advertising purposes.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Data Retention</h2>
              <p className="text-gray-300 leading-relaxed">
                We keep contact submissions for a limited time. Unless needed for legal reasons, we delete all personal data after <strong className="text-white">30 days</strong>. After deletion, the information cannot be retrieved.
              </p>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">6. Data Security</h2>
              <p className="text-gray-300 leading-relaxed mb-3">
                We use reasonable technical and organizational measures to protect your data against unauthorized access, loss, alteration or destruction (e.g. encryption at rest, secure server, HTTPS).
              </p>
              <p className="text-gray-300 leading-relaxed">
                However, no system is completely secure — in case of data breach, we will notify you if legally required.
              </p>
            </section>

            {/* Sharing or Disclosure */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. Sharing or Disclosure</h2>
              <p className="text-gray-300 leading-relaxed">
                We do not share your personal data with third parties. If we must do so for legal obligations (e.g. court order), we will inform accordingly.
              </p>
            </section>

            {/* International Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">8. International Transfers</h2>
              <p className="text-gray-300 leading-relaxed">
                If any personal data is transferred outside the European Economic Area (EEA), we will ensure that appropriate safeguards are in place in accordance with applicable data protection laws.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">9. Your Rights</h2>
              <p className="text-gray-300 leading-relaxed mb-3">
                Under GDPR (and similar laws), you have the following rights regarding your personal data:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Right of access (you can request a copy)</li>
                <li>Right to rectification (correct inaccuracies)</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to object to processing</li>
                <li>Right to data portability (if applicable)</li>
                <li>The right to withdraw consent at any time (this does not affect processing already done)</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-3">
                To exercise these rights, contact us at <a href="mailto:info@ereace.es" className="text-ereace-blue hover:underline">info@ereace.es</a>.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">10. Changes to This Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. The "effective date" will be updated. We encourage you to review it periodically.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">11. Contact</h2>
              <p className="text-gray-300 leading-relaxed">
                If you have questions or concerns about your data, please contact:
              </p>
              <p className="text-gray-300 mt-2">
                <strong className="text-white">Ereace</strong><br />
                Email: <a href="mailto:info@ereace.es" className="text-ereace-blue hover:underline">info@ereace.es</a><br />
                Website: <a href="https://ereace.es" className="text-ereace-blue hover:underline">https://ereace.es</a>
              </p>
            </section>

            {/* Back to Home Link */}
            <div className="mt-12 pt-8 border-t border-ereace-light-gray">
              <Link 
                to="/" 
                className="inline-flex items-center text-ereace-blue hover:text-white transition-colors"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-ereace-dark border-t border-ereace-light-gray py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Ereace. Building the future with AI.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;

